<template>
  <div class="custom-vue-time-picker ps__child--consume">
    <VueDatePicker
      ref="datepicker"
      :id="resolvedInputId"
      :aria-labels="ariaLabelsForPicker"
      :model-type="format ? format : 'hh:mm'"
      input-class-name="form-control"
      autocomplete="off"
      auto-apply
      :format="pickerTimeFormat ? pickerTimeFormat : 'hh:mm aa'"
      :is-24="!!showSeconds"
      text-input
      time-picker
      :clearable="false"
      :hide-input-icon="hideIcon"
      :class="classes"
      input-read-only
      @update:model-value="onValueChange"
      :model-value="modelValue"
      :placeholder="placeholder"
      :enable-seconds="!!showSeconds"
      :disabled="isDisabled"
      @open="onFocus"
      @blur="onBlur"
      :on-click-outside="onClickOutside"
      @input="(event) => onInput(event.target._value)"
    >
      <template #input-icon>
        <i class="fi flaticon-wall-clock"></i>
      </template>
      <template #arrow-up>
        <i class="fi flaticon-up-arrow"></i>
      </template>
      <template #arrow-down>
        <i class="fi flaticon-down-arrow"></i>
      </template>
      <template #clock-icon> </template>
    </VueDatePicker>
    <slot name="footer"></slot>
  </div>
</template>
<script>
import {
  ref,
  watch,
  reactive,
  computed,
  getCurrentInstance,
  watchEffect,
  nextTick
} from "vue"
import moment from "moment-timezone"
import "@vuepic/vue-datepicker/dist/main.css"

let customTimePickerFallbackId = 0
const INPUT_ICON_SELECTOR = ".dp__input_icon"
const normalizeLabelText = (value) => {
  const raw = String(value || "").trim()
  if (!raw) return ""
  return raw.replace(/\s+/g, " ").replace(/:+$/, "").trim()
}

export default {
  name: "CustomTimePicker",
  props: {
    value: {},
    placeholder: {},
    classes: {},
    readonly: {},
    disabled: {},
    format: {},
    hideIcon: {},
    innerLabel: {},
    disableSetFocus: {},
    modelValue: {},
    show: {},
    showSeconds: {},
    pickerTimeFormat: {},
    inputId: {},
    accessibleLabel: {},
    inputFormat12h: {
      default: true
    }
  },
  emits: ["update:modelValue", "input", "change", "focus", "blur"],
  setup(props, { emit }) {
    const instanceUid =
      getCurrentInstance()?.uid ?? ++customTimePickerFallbackId
    const resolvedInputId = computed(
      () => props.inputId || `cs-picker-${instanceUid}`
    )
    const ariaLabelsForPicker = computed(() => {
      const label =
        normalizeLabelText(props.accessibleLabel) ||
        normalizeLabelText(props.innerLabel) ||
        normalizeLabelText(props.placeholder) ||
        "Time"
      return {
        input: label
      }
    })
    const resolvedAccessibleLabel = computed(() => {
      return ariaLabelsForPicker.value?.input || "Time"
    })
    const isDisabled = computed(() => !!(props.disabled || props.readonly))

    const applyInputIconAccessibility = (rootEl, labelText, disabledState) => {
      const iconTrigger = rootEl?.querySelector?.(INPUT_ICON_SELECTOR)
      if (!iconTrigger) return

      if (disabledState) {
        iconTrigger.setAttribute("role", "presentation")
        iconTrigger.setAttribute("tabindex", "-1")
        iconTrigger.setAttribute("aria-disabled", "true")
        iconTrigger.removeAttribute("aria-label")
        return
      }

      iconTrigger.setAttribute("role", "button")
      iconTrigger.setAttribute("tabindex", "0")
      iconTrigger.removeAttribute("aria-disabled")
      iconTrigger.setAttribute(
        "aria-label",
        `Open ${labelText || "time picker"}`
      )

      if (iconTrigger.dataset?.keyboardReady === "true") return

      iconTrigger.addEventListener("keydown", (event) => {
        const isEnter = event?.key === "Enter"
        const isSpace = event?.key === " " || event?.key === "Spacebar"
        if (!isEnter && !isSpace) return
        event.preventDefault()
        iconTrigger.click()
      })
      iconTrigger.dataset.keyboardReady = "true"
    }

    //refs
    const datepicker = ref()

    const state = reactive({
      inputData: null
    })

    const onInput = (data) => {
      if (props.inputFormat12h && !props.showSeconds) {
        if (!data.length) {
          return
        }
        const dataSpaced = data.match(/.{1,5}/g).join(" ")
        if (dataSpaced.length > 4) {
          state.inputData = moment(dataSpaced, "hh:mm aa").format("HH:mm")
        }
      }
    }

    //watcher
    watch(
      () => props.show,
      (value) => {
        setTimeout(() => {
          if (value) {
            datepicker.value.openMenu()
          }
        }, 0)
      }
    )

    //methods
    const onValueChange = (value) => {
      emit("update:modelValue", state.inputData || value)
      emit("change", state.inputData || value)
      state.inputData = null
    }
    const onBlur = () => {
      emit("blur")
    }
    const onFocus = () => {
      if (!props.modelValue) {
        emit(
          "update:modelValue",
          moment().format(props.format ? props.format : "h:mm A")
        )
        emit("change", moment().format(props.format ? props.format : "h:mm A"))
      }
    }

    const onClickOutside = () => {
      datepicker.value.closeMenu()
      emit("blur")
    }

    watchEffect(() => {
      const accessibleInputLabel = resolvedAccessibleLabel.value
      const disabledState = isDisabled.value
      nextTick(() => {
        const root = datepicker.value?.$el
        const input = root?.querySelector("input")
        if (!input) return
        input.setAttribute("aria-label", accessibleInputLabel)
        if (disabledState) {
          input.setAttribute("tabindex", "-1")
        } else {
          input.removeAttribute("tabindex")
        }
        applyInputIconAccessibility(root, accessibleInputLabel, disabledState)
      })
    })

    return {
      state,
      onInput,
      onValueChange,
      onFocus,
      onBlur,
      datepicker,
      onClickOutside,
      resolvedInputId,
      ariaLabelsForPicker,
      resolvedAccessibleLabel,
      isDisabled
    }
  }
}
</script>
<style lang="scss">
@import "src/assets/scss/_variables";
.custom-vue-time-picker {
  .dp__instance_calendar {
    max-height: 200px;
  }
  .dp__time_display {
    font-size: 20px !important;
    padding: 20px 10px !important;
  }
  .dp__pm_am_button {
    background-color: $accent-blue-dark !important;
    padding: 8px 12px !important;
    border-radius: 15px !important;
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset !important;
      z-index: 1 !important;
    }
  }
  .dp__inc_dec_button {
    font-size: 20px;
    padding: 22px;
    border-radius: 0px;
    color: $gray-darken-50 !important;
    background-color: $white !important;
    &:hover {
      color: $accent-blue-dark;
    }
  }
  .dp__overlay_container {
    height: 100% !important;
  }
  .dp__button.dp__overlay_action {
    display: none;
  }
  .form-control,
  .dp__input {
    border-color: $accent-blue-dark !important;
    padding: $input-padding-y $input-padding-x !important;
    min-height: 40px;
    height: auto !important;
    border-radius: $input-border-radius;
    border-width: $input-border-width;
    font-size: $formfieldsFontSize;
    line-height: 1.5;
    font-weight: 400;
    color: $gray-darken-50 !important;
    background-color: $white !important;
    &::placeholder {
      color: $gray-darken-50 !important;
      opacity: 1;
    }
    &::-webkit-input-placeholder {
      color: $gray-darken-50 !important;
      opacity: 1;
    }
    &::-moz-placeholder {
      color: $gray-darken-50 !important;
      opacity: 1;
    }
    &:focus {
      outline: none;
      box-shadow: none !important;
    }
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset;
    }
  }
  .dp__input_icon {
    color: $accent-blue-dark !important;
    left: auto;
    right: 15px;
    display: flex;
    align-items: center;
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset;
      border-radius: 4px;
    }
    i {
      pointer-events: none;
    }
  }

  &.is-invalid {
    .form-control {
      border-color: #e55353 !important;
    }

    .invalid-feedback {
      display: block;
      z-index: 1;
    }
  }

  .timepicker {
    z-index: 999;

    .btn {
      box-shadow: none !important;

      &.btn-primary {
        padding: 12px;
      }
    }
  }

  .bootstrap-datetimepicker-widget a {
    padding: 0 !important;
  }
}
</style>
